import type {
  ContentRepositoryMapping,
  RepositoryMappingAggregateType,
  RepositoryMappings,
  ThemeRepositoryMapping,
} from '@cms/contracts'

export interface SourceBodyDraft {
  repositoryPath: string
  finalNewline: boolean
  format: 'html' | 'markdown'
  dataPath: string
}

export interface RepositoryMappingDraft {
  enabled: boolean
  metadataPath: string
  metadataFinalNewline: boolean
  sourceBodies: SourceBodyDraft[]
  authoredCssEnabled: boolean
  authoredCssPath: string
  authoredCssFinalNewline: boolean
}

export function emptyRepositoryMappingDraft(): RepositoryMappingDraft {
  return {
    enabled: false,
    metadataPath: '',
    metadataFinalNewline: true,
    sourceBodies: [],
    authoredCssEnabled: false,
    authoredCssPath: '',
    authoredCssFinalNewline: true,
  }
}

export function repositoryMappingDraft<T extends RepositoryMappingAggregateType>(
  aggregateType: T,
  mapping: RepositoryMappings[T] | null,
): RepositoryMappingDraft {
  if (!mapping) return emptyRepositoryMappingDraft()
  const content = aggregateType === 'Content' ? mapping as ContentRepositoryMapping : null
  const theme = aggregateType === 'Theme' ? mapping as ThemeRepositoryMapping : null
  return {
    enabled: true,
    metadataPath: mapping.metadataPath,
    metadataFinalNewline: mapping.metadataFinalNewline,
    sourceBodies: content?.sourceBodies.map((body) => ({ ...body })) ?? [],
    authoredCssEnabled: theme?.authoredCss !== null && theme?.authoredCss !== undefined,
    authoredCssPath: theme?.authoredCss?.repositoryPath ?? '',
    authoredCssFinalNewline: theme?.authoredCss?.finalNewline ?? true,
  }
}

export function repositoryMappingValue<T extends RepositoryMappingAggregateType>(
  aggregateType: T,
  draft: RepositoryMappingDraft,
): RepositoryMappings[T] | null {
  if (!draft.enabled) return null
  const metadata = {
    metadataPath: draft.metadataPath.trim(),
    metadataFinalNewline: draft.metadataFinalNewline,
  }
  if (aggregateType === 'Content') {
    return { ...metadata, sourceBodies: draft.sourceBodies.map((body) => ({
      ...body,
      repositoryPath: body.repositoryPath.trim(),
      dataPath: body.dataPath.trim(),
    })) } as RepositoryMappings[T]
  }
  if (aggregateType === 'Theme') {
    return {
      ...metadata,
      authoredCss: draft.authoredCssEnabled ? {
        repositoryPath: draft.authoredCssPath.trim(),
        finalNewline: draft.authoredCssFinalNewline,
      } : null,
    } as RepositoryMappings[T]
  }
  return metadata as RepositoryMappings[T]
}

export function repositoryMappingErrors(aggregateType: RepositoryMappingAggregateType, draft: RepositoryMappingDraft): string[] {
  if (!draft.enabled) return []
  const errors: string[] = []
  if (!draft.metadataPath.trim()) errors.push('metadataPath')
  if (aggregateType === 'Content') {
    draft.sourceBodies.forEach((body, index) => {
      if (!body.repositoryPath.trim()) errors.push(`sourceBodies.${index}.repositoryPath`)
      if (!body.dataPath.trim()) errors.push(`sourceBodies.${index}.dataPath`)
    })
  }
  if (aggregateType === 'Theme' && draft.authoredCssEnabled && !draft.authoredCssPath.trim()) errors.push('authoredCss.repositoryPath')
  return errors
}

export function sameRepositoryMappingDraft(left: RepositoryMappingDraft, right: RepositoryMappingDraft): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export function showPrimarySavebar(mappingExpanded: boolean, primaryDirty: boolean): boolean {
  return !mappingExpanded || primaryDirty
}
